import { ref, reactive } from 'vue'

// Estado global de configuración
const config = reactive({
  api: {
    baseURL: 'http://localhost:8000',
    timeout: 60000 // 60 segundos
  },
  auth: {
    tokenCheckInterval: 120000, // 2 minutos
    tokenRenewalThreshold: 1800000, // 30 minutos
    tokenRenewalInterval: 60000 // 1 minuto
  },
  app: {
    name: 'Doox',
    version: '1.0.0',
    debug: false
  }
})

const isLoading = ref(false)
const isLoaded = ref(false)

/**
 * Composable para manejar la configuración de la aplicación
 * @returns {Object} Objeto con la configuración y métodos para cargarla
 */
export function useConfig() {
  /**
   * Carga la configuración desde el archivo config.json
   */
  const loadConfig = async () => {
    if (isLoaded.value) return config
    
    isLoading.value = true
    
    try {
      const response = await fetch('/config.json')
      if (!response.ok) {
        throw new Error(`Error al cargar configuración: ${response.status}`)
      }
      
      const configData = await response.json()
      
      // Actualizar la configuración con los datos del archivo
      Object.assign(config, configData)
      isLoaded.value = true
    } catch (error) {
      console.warn('No se pudo cargar la configuración externa, usando valores por defecto:', error.message)
      isLoaded.value = true
    } finally {
      isLoading.value = false
    }
    
    return config
  }

  /**
   * Obtiene un valor de configuración por ruta
   * @param {string} path - Ruta del valor (ej: 'api.baseURL')
   * @returns {any} Valor de configuración
   */
  const getConfig = (path) => {
    return path.split('.').reduce((obj, key) => obj?.[key], config)
  }

  /**
   * Actualiza un valor de configuración
   * @param {string} path - Ruta del valor (ej: 'api.baseURL')
   * @param {any} value - Nuevo valor
   */
  const setConfig = (path, value) => {
    const keys = path.split('.')
    const lastKey = keys.pop()
    const target = keys.reduce((obj, key) => obj[key] = obj[key] || {}, config)
    target[lastKey] = value
  }

  return {
    config,
    isLoading,
    isLoaded,
    loadConfig,
    getConfig,
    setConfig
  }
}
